import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { parentsApi } from '@/api/endpoints/parents'
import { type UserResponse } from '@/api/endpoints/users'
import { useUI } from '@/stores/ui'
import Spinner from '@/components/common/Spinner'

interface ParentWithChildren extends UserResponse {
  parent_id?: string
  children_count: number
}

interface DuplicateGroup {
  parents: ParentWithChildren[]
  matchReason: string
}

/**
 * Parents with their *parent-profile* id as `id` — the merge endpoint needs profile ids.
 * (It used to send user ids, so every merge failed with "Parent not found".)
 */
async function loadParents(): Promise<ParentWithChildren[]> {
  const list = await parentsApi.list()
  return list.map(p => ({
    ...(p as any),
    id: p.id,
    parent_id: p.id,
    children_count: p.children_count ?? 0,
  }))
}

export default function MergeDoubleParentTool() {
  const addToast = useUI((state) => state.addToast)
  const [parents, setParents] = useState<ParentWithChildren[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<DuplicateGroup | null>(null)
  const [merging, setMerging] = useState(false)

  useEffect(() => {
    let mounted = true
    const load = async () => {
      setLoading(true)
      try {
        if (mounted) setParents(await loadParents())
      } catch (error) {
        console.error('Failed to load parents:', error)
        if (mounted) setParents([])
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => { mounted = false }
  }, [])

  // Find duplicate groups: matching first name AND last name AND (email OR phone OR address)
  const duplicateGroups = useMemo(() => {
    const groups: DuplicateGroup[] = []
    const processed = new Set<string>()

    for (let i = 0; i < parents.length; i++) {
      const parent1 = parents[i]
      const groupKey = `${parent1.first_name}-${parent1.last_name}`.toLowerCase()

      if (processed.has(groupKey)) continue

      const matches: ParentWithChildren[] = [parent1]

      for (let j = i + 1; j < parents.length; j++) {
        const parent2 = parents[j]

        // Check if first name and last name match (case-insensitive)
        const nameMatch = 
          parent1.first_name?.toLowerCase() === parent2.first_name?.toLowerCase() &&
          parent1.last_name?.toLowerCase() === parent2.last_name?.toLowerCase()

        if (nameMatch) {
          // Check if email, phone, or address matches
          const emailMatch = parent1.email && parent2.email && parent1.email.toLowerCase() === parent2.email.toLowerCase()
          const phoneMatch = parent1.cell_phone && parent2.cell_phone && parent1.cell_phone === parent2.cell_phone
          const addressMatch = parent1.address && parent2.address && parent1.address.toLowerCase() === parent2.address.toLowerCase()

          if (emailMatch || phoneMatch || addressMatch) {
            if (!matches.find(p => p.id === parent2.id)) {
              matches.push(parent2)
            }
            let reason = 'Matches: '
            if (emailMatch) reason += 'email'
            if (phoneMatch) reason += (emailMatch ? ', ' : '') + 'phone'
            if (addressMatch) reason += ((emailMatch || phoneMatch) ? ', ' : '') + 'address'

            // Add to group if not already added
            const existingGroup = groups.find(g =>
              g.parents.some(p => p.id === parent1.id || p.id === parent2.id)
            )

            if (existingGroup) {
              if (!existingGroup.parents.find(p => p.id === parent2.id)) {
                existingGroup.parents.push(parent2)
              }
            } else if (matches.length > 1) {
              groups.push({
                parents: matches,
                matchReason: reason,
              })
              processed.add(groupKey)
            }
          }
        }
      }
    }

    return groups.filter(g => g.parents.length > 1)
  }, [parents])

  const handleMerge = async (primaryId: string, duplicateId: string) => {
    if (!confirm('Are you sure you want to merge these parent accounts? This will move all children from the duplicate account to the primary account.')) {
      return
    }

    setMerging(true)
    try {
      const result = await parentsApi.merge(primaryId, duplicateId)
      addToast({
        message: result.message || `Successfully merged! ${result.children_moved || 0} children moved.`,
        type: 'success',
      })
      // Refresh parents list
      setParents(await loadParents())
      setSelectedGroup(null)
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to merge parents',
        type: 'error',
      })
    } finally {
      setMerging(false)
    }
  }

  return (
    <motion.div
      className="mt-6 p-5 rounded-xl border border-gray-200 bg-white shadow-sm hover-raise"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <h3 className="text-lg font-semibold mb-3">Merge Double-Parent Registration</h3>
      <p className="text-sm text-gray-600 mb-4">
        Find and merge duplicate parent accounts where the same parent has registered multiple times.
      </p>

      {loading && <Spinner label="Loading parents..." />}
      
      {!loading && duplicateGroups.length === 0 && (
        <div className="text-sm text-gray-500">No duplicate parent registrations found.</div>
      )}

      {!loading && duplicateGroups.length > 0 && (
        <div className="space-y-4">
          {duplicateGroups.map((group, groupIdx) => (
            <div key={groupIdx} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-semibold">
                    {group.parents[0].first_name} {group.parents[0].last_name}
                  </div>
                  <div className="text-xs text-gray-600">{group.matchReason}</div>
                </div>
                <button
                  className="btn-primary text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  onClick={() => setSelectedGroup(selectedGroup?.parents === group.parents ? null : group)}
                  disabled={merging}
                  title="Review duplicate parent accounts and merge them into a single account. All children will be moved to the primary account."
                >
                  {selectedGroup?.parents === group.parents ? 'Hide' : 'Review & Merge'}
                </button>
              </div>

              <AnimatePresence>
                {selectedGroup?.parents === group.parents && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
                      <p className="text-xs text-gray-600 mb-2">
                        Select which account to keep (primary) and which to merge (duplicate):
                      </p>
                      {group.parents.map((parent, _idx) => (
                        <div key={parent.id} className="p-3 bg-gray-50 rounded border border-gray-200">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="font-medium">{parent.first_name} {parent.last_name}</div>
                              <div className="text-xs text-gray-600 mt-1">
                                Email: {parent.email || 'N/A'}<br />
                                Phone: {parent.cell_phone || 'N/A'}<br />
                                Address: {parent.address || 'N/A'}<br />
                                Children: {parent.children_count || 0}
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              {group.parents.map((otherParent, _otherIdx) => {
                                if (otherParent.id === parent.id) return null
                                return (
                                  <button
                                    key={otherParent.id}
                                    className="btn-secondary text-xs whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                                    onClick={() => handleMerge(parent.id, otherParent.id)}
                                    disabled={merging}
                                    title="Merge all children from the duplicate account into the primary account. The duplicate account will be removed."
                                  >
                                    Keep {parent.email} (merge {otherParent.email} into it)
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  )
}

